import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { selectCourseConfig } from '../../store/appDataStore';
import { selectUserInfos } from '../../store/userDataStore';

import { useTheme } from '@mui/material/styles';
import { Drawer, List, ListItem, ListItemButton, ListItemText, Toolbar, ListItemIcon, CircularProgress } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SchoolIcon from '@mui/icons-material/School';
import LogoutIcon from '@mui/icons-material/Logout';
import InsertChartIcon from '@mui/icons-material/InsertChart';
import RefreshIcon from '@mui/icons-material/Refresh';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import GroupIcon from '@mui/icons-material/Group';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import SyncIcon from '@mui/icons-material/Sync';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import DateRangeIcon from '@mui/icons-material/DateRange';
import CodeIcon from '@mui/icons-material/Code';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import { useAuth } from '../../providers/AuthProvider';
import styles from './DrawerMenu.module.css';
import { useAppClasses } from '../../providers/MuiThemeProvider';
import { requestDataRefresh } from '../../store/userDataStore';


const DrawerMenu: React.FC<{ }> = ({ }) => {
  const drawerWidth = 200;
  const theme = useTheme();
  const location = useLocation();
  const { logout } = useAuth();
  const courseConfig = useSelector(selectCourseConfig);
  const userInfos = useSelector(selectUserInfos);
  const isAdmin = userInfos?.access === 'admin';
  const isTeacher = userInfos?.access === 'teacher' || isAdmin;
  const classes = useAppClasses();
  const dispatch = useDispatch();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Les pages qui affichent des résultats CI (Charts, résultats d'un cycle) rechargent leurs
  // données : plus de téléchargement de tous les résultats de tous les groupes
  const handleRefreshCycleResults = () => {
    setIsRefreshing(true);
    dispatch(requestDataRefresh());
    setTimeout(() => setIsRefreshing(false), 400);
  };

  return (
      <Drawer
        variant="permanent"
        slotProps={{ paper: { className: styles['drawer-menu-paper'], style: { background: theme.palette.primary.dark, width: `${drawerWidth}px`, minWidth: 180 } } }}
        sx={{ width: `${drawerWidth}px`, minWidth: 200, flexShrink: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
      >
        <div>
          <div className={styles['drawer-menu-title']}>
            <span className={styles['drawer-menu-title-main']}>PWA</span><br />
            <span className={styles['drawer-menu-title-sub']}>Dashboard</span>
          </div>
          <Toolbar />
          <List>
            <ListItem disablePadding>
              <ListItemButton 
                component={Link}
                to="/"
                selected={location.pathname === '/'}
              >
                <ListItemIcon className={styles['drawer-menu-icon']}><HomeIcon /></ListItemIcon>
                <ListItemText 
                  primary="Vue globale" 
                  slotProps={{ primary: { className: classes.textBold } }}
                />
              </ListItemButton>
            </ListItem>
            {courseConfig?.cycles?.map((cycle: any) => (
              <ListItem key={cycle.id} disablePadding>
                <ListItemButton
                  component={Link}
                  to={`/cycle/${cycle.id}`}
                  selected={location.pathname === `/cycle/${cycle.id}`}>
                  <ListItemIcon className={styles['drawer-menu-icon']}>{cycle.short_name === 'project' ? <SchoolIcon /> :  <AssignmentIcon />}</ListItemIcon>
                  <ListItemText 
                    primary={cycle.short_name?.toUpperCase()} 
                    slotProps={{ primary: { className: classes.textBold } }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/charts"
                selected={location.pathname.startsWith('/charts')}
              >
                <ListItemIcon className={styles['drawer-menu-icon']}><InsertChartIcon /></ListItemIcon>
                <ListItemText primary="Charts" slotProps={{ primary: { className: classes.textBold } }} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/assiduite"
                selected={location.pathname.startsWith('/assiduite')}
              >
                <ListItemIcon className={styles['drawer-menu-icon']}><EventAvailableIcon /></ListItemIcon>
                <ListItemText primary="Assiduité" slotProps={{ primary: { className: classes.textBold } }} />
              </ListItemButton>
            </ListItem>
            {isTeacher && (
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to="/revue-code"
                  selected={location.pathname.startsWith('/revue-code')}
                >
                  <ListItemIcon className={styles['drawer-menu-icon']}><CodeIcon /></ListItemIcon>
                  <ListItemText primary="Revue de code" slotProps={{ primary: { className: classes.textBold } }} />
                </ListItemButton>
              </ListItem>
            )}
            {isTeacher && (
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to="/suivi-moodle"
                  selected={location.pathname.startsWith('/suivi-moodle')}
                >
                  <ListItemIcon className={styles['drawer-menu-icon']}><QueryStatsIcon /></ListItemIcon>
                  <ListItemText primary="Suivi Moodle" slotProps={{ primary: { className: classes.textBold } }} />
                </ListItemButton>
              </ListItem>
            )}
            {isAdmin && (
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to="/admin/gitlab"
                  selected={location.pathname.startsWith('/admin/gitlab')}
                >
                  <ListItemIcon className={styles['drawer-menu-icon']}><AdminPanelSettingsIcon /></ListItemIcon>
                  <ListItemText primary="Admin GitLab" slotProps={{ primary: { className: classes.textBold } }} />
                </ListItemButton>
              </ListItem>
            )}
            {isAdmin && (
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to="/admin/users"
                  selected={location.pathname.startsWith('/admin/users')}
                >
                  <ListItemIcon className={styles['drawer-menu-icon']}><GroupIcon /></ListItemIcon>
                  <ListItemText primary="Admin Utilisateurs" slotProps={{ primary: { className: classes.textBold } }} />
                </ListItemButton>
              </ListItem>
            )}
            {isAdmin && (
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to="/admin/groups"
                  selected={location.pathname.startsWith('/admin/groups')}
                >
                  <ListItemIcon className={styles['drawer-menu-icon']}><GroupWorkIcon /></ListItemIcon>
                  <ListItemText primary="Admin Groupes" slotProps={{ primary: { className: classes.textBold } }} />
                </ListItemButton>
              </ListItem>
            )}
            {isAdmin && (
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to="/admin/tokens"
                  selected={location.pathname.startsWith('/admin/tokens')}
                >
                  <ListItemIcon className={styles['drawer-menu-icon']}><VpnKeyIcon /></ListItemIcon>
                  <ListItemText primary="Tokens d'API" slotProps={{ primary: { className: classes.textBold } }} />
                </ListItemButton>
              </ListItem>
            )}
            {isAdmin && (
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to="/admin/cycles"
                  selected={location.pathname.startsWith('/admin/cycles')}
                >
                  <ListItemIcon className={styles['drawer-menu-icon']}><DateRangeIcon /></ListItemIcon>
                  <ListItemText primary="Admin Cycles" slotProps={{ primary: { className: classes.textBold } }} />
                </ListItemButton>
              </ListItem>
            )}
            {isAdmin && (
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to="/admin/moodle-sync"
                  selected={location.pathname.startsWith('/admin/moodle-sync')}
                >
                  <ListItemIcon className={styles['drawer-menu-icon']}><SyncIcon /></ListItemIcon>
                  <ListItemText primary="Synchro Moodle" slotProps={{ primary: { className: classes.textBold } }} />
                </ListItemButton>
              </ListItem>
            )}
          </List>
        </div>
        <div className={styles['drawer-menu-logout']}>
          <List>
            <ListItem disablePadding>
              <ListItemButton onClick={handleRefreshCycleResults} disabled={isRefreshing}>
                <ListItemIcon className={styles['drawer-menu-icon']}>
                  {isRefreshing ? <CircularProgress size={24} /> : <RefreshIcon />}
                </ListItemIcon>
                <ListItemText
                  primary="Rafraîchir Data"
                  slotProps={{ primary: { className: classes.textBold } }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={logout}>
                <ListItemIcon className={styles['drawer-menu-icon']}><LogoutIcon /></ListItemIcon>
                <ListItemText
                  primary="Déconnexion"
                  slotProps={{ primary: { className: classes.textBold } }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </div>
    </Drawer>
  );
};

export default DrawerMenu;
